// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import Check from "@mcmc/XDL/Icons/Check";
import { mergeWithCss, RobotoCondensed } from "@mcmc/XDL/Utilities";
import * as React from "react";
import { useState } from "react";
import { css } from "styled-components";

export interface ProgressStepProps {
    index: number;
    current: number;
    label?: string;
    icon?: React.ComponentType | string;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProgressStepStyles;
    buttonType?: string;
    disabled?: boolean;
}

type Props = ProgressStepProps;

type StepVariant = "start" | "next" | "current" | "passed";

export interface ProgressStepStyles {
    step: {
        start?: InjectableCss;
        next?: InjectableCss;
        current?: InjectableCss;
        passed?: InjectableCss;
    };
    arrow?: InjectableCss;
    startIcon?: IconPresentationProps;
    stepLabel?: TypographyPresentationProps;
    checkIcon?: IconPresentationProps;
}

const getStepStyle = (backgroundColor: string, injectedCss?: StyledProp): InjectableCss => {
    return {
        css: css`
            position: relative;
            flex: 1 1 auto;
            height: 35px;
            background-color: ${getColor(backgroundColor)};
            padding: 0 10px 0 20px;
            display: flex;
            align-items: center;
            justify-content: center;

            &:last-child {
                padding-right: 20px;
                .step-arrow {
                    display: none;
                }
            }

            .step-label {
                color: ${getColor("text")};
            }

            .step-arrow {
                border-left-color: ${getColor(backgroundColor)};
            }

            .check-icon {
                display: none;
                position: relative;
                top: 1px;
            }

            > button {
                transition: transform 0.2s;

                &:active,
                &:focus,
                &:hover {
                    transform: scale(1.1);
                    outline: none;
                }
            }

            > span {
                cursor: default;
                &:active,
                &:focus {
                    outline: none;
                }
            }

            ${injectedCss}
        `,
    };
};

export const progressStepStyles: ProgressStepStyles = {
    step: {
        start: getStepStyle(
            "primary",
            css`
                flex: 0 0 auto;
                padding: 0 10px 0 15px;
            `,
        ),
        passed: getStepStyle(
            "secondary",
            css`
                .step-label {
                    color: ${getColor("secondary.contrast")};
                }

                .step-index {
                    display: none;
                }

                .check-icon {
                    display: inline;
                }
            `,
        ),
        current: getStepStyle("common.ui3"),
        next: getStepStyle(
            "common.ui3",
            css`
                .step-label {
                    opacity: 0.5;
                }
            `,
        ),
    },
    arrow: {
        css: css`
            position: absolute;
            left: 100%;
            top: 0;
            border: 17px solid transparent;
            border-left: 12px solid transparent;
            z-index: 1;
            height: 100%;
            display: block;
            pointer-events: none;

            &::before,
            &::after {
                content: "";
                position: absolute;
                left: -6px;
                display: block;
                width: 2px;
                height: 23px;
                background-color: ${getColor("common.background")};
            }

            &::before {
                transform: rotate(-35deg);
                top: -20px;
            }

            &::after {
                transform: rotate(35deg);
                top: -2px;
            }
        `,
    },
    startIcon: {
        size: 20,
        color: "primary.contrast",
    },
    stepLabel: {
        ...RobotoCondensed,
        size: 14,
        weight: "bold",
        lineHeight: "16px",
        css: css`
            letter-spacing: 0;
            text-transform: uppercase;
            white-space: nowrap;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    checkIcon: {
        src: Check,
        size: 13,
    },
};

const ProgressStep: React.FC<Props> = ({ index, current, label, icon, extendedStyles, buttonType, ...otherProps }) => {
    const [styles] = useState(mergeWithCss(progressStepStyles, extendedStyles));

    let variant: StepVariant = "next";
    if (index === 0) {
        variant = "start";
    } else if (index < current) {
        variant = "passed";
    } else if (index === current) {
        variant = "current";
    }

    return (
        <StyledWrapper {...styles.step[variant]}>
            <Link type={buttonType} {...otherProps}>
                {!label && <Icon src={icon} {...styles.startIcon} />}
                {label && (
                    <Typography {...styles.stepLabel} className="step-label">
                        <Icon {...styles.checkIcon} aria-hidden="true" className="check-icon" />
                        <span className="step-index">{index}.</span>
                        &nbsp;{label}
                    </Typography>
                )}
            </Link>

            <StyledWrapper as="span" {...styles.arrow} className="step-arrow" aria-hidden="true"></StyledWrapper>
        </StyledWrapper>
    );
};

export default ProgressStep;
